import { postMessage, checkPostMessageAvailable } from './appPostMessage';
import * as api from '../api';
import * as taxiTripAPI from '../api/taxiTrip';

// 全局認證鎖，防止多次同時認證
let authPromise = null;
// 全局重整鎖，防止多次重整頁面
let isReloading = false;

/**
 * 取得 inApp 認證的 fallback URL
 * @returns {string} 導向認證頁面的完整 URL
 */
export const getInAppAuthUrl = () => {
  return `https://inapp.kingnetsmart.com.tw/?client=${process.env.REACT_APP_KNST_CLIENT}&targeturl=${process.env.REACT_APP_API_ENDPOINT}&browsertitle=${process.env.REACT_APP_BROWSER_TITLE}`;
};

/**
 * 透過 postMessage 向 App 請求認證 code
 * @returns {Promise<string|null>} 回傳 code 或 null（如果失敗或不支援）
 */
export const requestCodeFromApp = () => {
  return new Promise((resolve) => {
    // 檢查 postMessage 是否可用
    if (!checkPostMessageAvailable()) {
      console.log('postMessage 不支援，將使用 fallback 導向');
      resolve(null);
      return;
    }

    // 設定 timeout，若超時則回傳 null
    const timeoutId = setTimeout(() => {
      window.removeEventListener('inAppReceiveCode', handleReceiveCode);
      console.log('等待 inAppReceiveCode 超時');
      resolve(null);
    }, 5000);

    // 監聽 App 回傳的 code
    const handleReceiveCode = (event) => {
      clearTimeout(timeoutId);
      window.removeEventListener('inAppReceiveCode', handleReceiveCode);
      const expectedClientId = process.env.REACT_APP_KNST_CLIENT;
      const code = event?.detail?.code;
      const clientId = event?.detail?.clientId;

      if (expectedClientId && clientId && clientId !== expectedClientId) {
        console.warn('inAppReceiveCode clientId 驗證失敗');
        resolve(null);
        return;
      }

      if (code) {
        console.log('收到 inAppReceiveCode:', code);
        resolve(code);
      } else {
        console.warn('inAppReceiveCode 事件缺少 code');
        resolve(null);
      }
    };

    window.addEventListener('inAppReceiveCode', handleReceiveCode);

    // 發送 postMessage 給 App
    const message = {
      type: 'inAppAuthRequest',
      clientId: process.env.REACT_APP_KNST_CLIENT,
      targetUrl: process.env.REACT_APP_API_ENDPOINT,
      browserTitle: process.env.REACT_APP_BROWSER_TITLE,
    };

    const postResult = postMessage(message);
    if (!postResult) {
      clearTimeout(timeoutId);
      window.removeEventListener('inAppReceiveCode', handleReceiveCode);
      console.log('postMessage 發送失敗');
      resolve(null);
    }
  });
};

/**
 * 使用 code 換取 token 並設定到 API header
 * 純粹只負責 token 交換，不處理清除資料和頁面重整
 * @param {string} code - 從 App 取得的認證 code
 * @returns {Promise<{success: boolean, token?: string, error?: any}>}
 */
export const exchangeCodeForToken = async (code) => {
  try {
    const response = await api.kingnetTaxiToken({ code });
    if (response.data.Status === 1) {
      const token = response.data.Data.Token;
      const now = new Date();
      now.setHours(now.getHours() + 1);

      // 設定新 token（會覆蓋舊的）
      window.localStorage.setItem('ecsToken', token);
      window.localStorage.setItem('ecsTokenExpireTime', now);
      api.setAuthHeader(token);
      taxiTripAPI.setAuthHeader(token);
      api.setEcsAuthHeader(token);

      return { success: true, token };
    } else {
      console.error('Token 交換失敗:', response.data.ErrorMessage);
      return { success: false, error: response.data.ErrorMessage };
    }
  } catch (error) {
    console.error('Token 交換錯誤:', error);
    return { success: false, error };
  }
};

/**
 * 清除所有使用者相關資料
 * 同步操作，不涉及任何 async/await
 */
function clearAllUserData() {
  window.localStorage.removeItem('overServiceArea');
  window.localStorage.removeItem('GUID');
  window.localStorage.removeItem('pageStatus');
  window.localStorage.removeItem('getCodeTime');
  window.localStorage.removeItem('ServiceAccountAId');
  window.localStorage.removeItem('ecsToken');
  window.localStorage.removeItem('ecsTokenExpireTime');
  window.sessionStorage.removeItem('watchStatus');
}

/**
 * 執行 inApp 認證流程
 * 優先使用 postMessage 向 App 請求 code，若不支援則使用 URL 導向
 * 使用全局鎖機制，防止多次同時認證
 *
 * 流程：
 * 1. 拿到新 code（透過 postMessage 或 fallback URL）
 * 2. 清除所有舊資料
 * 3. 將 code 帶在 URL 上重新載入頁面 → `/?code=${code}`
 * 4. 頁面重新載入後，App.js 會從 URL 取得 code 並換成 token
 *
 * @returns {Promise<void>}
 */
export const requestInAppAuth = async () => {
  // 如果已有認證流程在進行，等待其完成
  if (authPromise) {
    console.log('🔒 已有認證流程進行中，等待完成...');
    return authPromise;
  }

  // 創建新的認證流程並加鎖
  authPromise = (async () => {
    try {
      // 優先使用 postMessage 請求 code
      const code = await requestCodeFromApp();

      if (!code) {
        // postMessage 不支援或失敗，使用 URL 導向 fallback
        console.log('使用 URL 導向 fallback');
        window.location.replace(getInAppAuthUrl());
        return;
      }

      // 拿到 code 後，清除所有舊資料並帶 code reload
      clearAllUserData();
      console.log('已清除所有使用者資料，準備帶 code 重新載入頁面...');

      if (!isReloading) {
        isReloading = true;
        // 將 code 帶在 URL 上重新載入（保留原有參數與路徑）
        const url = new URL(window.location.href);
        url.searchParams.set('code', code);
        window.location.replace(url.toString());
      }
    } catch (error) {
      console.error('requestInAppAuth 錯誤:', error);
      // 發生錯誤時使用 fallback
      window.location.replace(getInAppAuthUrl());
    }
  })();

  return authPromise;
};

export default {
  getInAppAuthUrl,
  requestCodeFromApp,
  exchangeCodeForToken,
  requestInAppAuth,
};
